import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, Legend
} from 'recharts';
import { 
  Target, TrendingUp, TrendingDown, AlertTriangle, Trophy, 
  Users, DollarSign, Lightbulb, Plus, Eye, Building2,
  ArrowUpRight, ArrowDownRight, Minus, RefreshCw, Shield,
  FileText, CheckCircle, X, Search
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OUTCOME_COLORS = {
  WON: '#22C55E',
  LOST: '#EF4444',
  RETAINED: '#3B82F6'
};

const REASON_COLORS = ['#09409C', '#146CBB', '#FF521C', '#229644', '#FFA500', '#8B5CF6'];

const IMPACT_CONFIG = {
  HIGH: { color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  MEDIUM: { color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
  LOW: { color: 'bg-green-100 text-green-700', icon: CheckCircle }
};

function TrendIndicator({ trend }) {
  if (trend === 'up') return <ArrowUpRight className="w-4 h-4 text-green-500" />;
  if (trend === 'down') return <ArrowDownRight className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-gray-400" />;
}

function ThreatBadge({ level }) {
  const config = {
    HIGH: 'bg-red-100 text-red-700 border-red-200',
    MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    LOW: 'bg-green-100 text-green-700 border-green-200'
  };
  return <Badge variant="outline" className={config[level] || config.MEDIUM}>{level} Threat</Badge>;
}

export default function CompetitiveIntelligence() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [showWinLossModal, setShowWinLossModal] = useState(false);
  const [showIntelModal, setShowIntelModal] = useState(false);
  const [regionFilter, setRegionFilter] = useState('all');

  // Fetch dashboard summary
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['competitive-summary'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/competitive/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch competitors
  const { data: competitorsData } = useQuery({
    queryKey: ['competitors'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/competitive/competitors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch win/loss analytics
  const { data: analyticsData } = useQuery({
    queryKey: ['win-loss-analytics'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/competitive/win-loss/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch market intelligence
  const { data: intelData, refetch: refetchIntel } = useQuery({
    queryKey: ['market-intelligence'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/competitive/intelligence`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  // Fetch competitor detail
  const { data: competitorDetail } = useQuery({
    queryKey: ['competitor-detail', selectedCompetitor],
    queryFn: async () => {
      if (!selectedCompetitor) return null;
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/competitive/competitors/${selectedCompetitor}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    enabled: !!selectedCompetitor,
  });

  const summary = summaryData?.summary || {};
  const competitors = competitorsData?.competitors || [];
  const analytics = analyticsData || {};
  const intelligence = intelData?.intelligence || [];

  // Prepare chart data
  const outcomeData = analytics.summary ? [
    { name: 'Won', value: analytics.summary.wins, color: OUTCOME_COLORS.WON },
    { name: 'Lost', value: analytics.summary.losses, color: OUTCOME_COLORS.LOST },
    { name: 'Retained', value: analytics.summary.retained, color: OUTCOME_COLORS.RETAINED },
  ] : [];

  const reasonData = (analytics.by_loss_reason || []).map((r, i) => ({
    name: r.reason.replace('_', ' '),
    value: r.count,
    fill: REASON_COLORS[i % REASON_COLORS.length]
  }));

  const competitorData = (analytics.by_competitor || []).slice(0, 5).map(c => ({
    name: c.competitor_name.length > 12 ? c.competitor_name.substring(0, 12) + '...' : c.competitor_name,
    wins: c.wins,
    losses: c.losses,
    win_rate: c.win_rate
  }));

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="loading-state">
        <RefreshCw className="w-8 h-8 animate-spin text-[#09409C]" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="competitive-intelligence-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="text-gradient-primary">Competitive Intelligence</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Track competitors, analyze win/loss patterns, and monitor market intelligence
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowIntelModal(true)} data-testid="add-intel-btn">
            <Lightbulb className="w-4 h-4 mr-2" />
            Add Intel
          </Button>
          <Button onClick={() => setShowWinLossModal(true)} data-testid="record-outcome-btn">
            <Plus className="w-4 h-4 mr-2" />
            Record Outcome
          </Button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-2 hover:shadow-lg transition-shadow" data-testid="kpi-win-rate">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Win Rate</p>
              <Trophy className="w-5 h-5 text-yellow-500" />
            </div>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold">{summary.win_rate || 0}%</p>
              <TrendIndicator trend={summary.win_rate_trend} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">This quarter</p>
          </CardContent>
        </Card>

        <Card className="border-2 hover:shadow-lg transition-shadow" data-testid="kpi-market-share">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Market Share</p>
              <Target className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold">{summary.estimated_market_share || 0}%</p>
            <p className="text-xs text-muted-foreground mt-1">Rank #{summary.market_share_rank || '-'}</p>
          </CardContent>
        </Card>

        <Card className="border-2 hover:shadow-lg transition-shadow" data-testid="kpi-competitors">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Competitors Tracked</p>
              <Users className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-3xl font-bold">{summary.total_competitors_tracked || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Active profiles</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-orange-200 hover:shadow-lg transition-shadow" data-testid="kpi-intel">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Action Required</p>
              <AlertTriangle className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-3xl font-bold text-orange-600">{summary.active_intel_items || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Intel items pending</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-red-200 hover:shadow-lg transition-shadow" data-testid="kpi-threat">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Top Threat</p>
              <Shield className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-lg font-bold truncate">{summary.top_threat?.name || 'N/A'}</p>
            <p className="text-xs text-red-600 mt-1">{summary.top_threat?.losses_to_them || 0} losses</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="competitors">Competitors</TabsTrigger>
          <TabsTrigger value="win-loss">Win/Loss Analysis</TabsTrigger>
          <TabsTrigger value="intelligence">Market Intel</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Win/Loss Distribution */}
            <Card data-testid="outcome-distribution">
              <CardHeader>
                <CardTitle className="text-lg">Outcome Distribution</CardTitle>
                <CardDescription>Win/loss/retained breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={outcomeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {outcomeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2">
                  {outcomeData.map((item) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm">{item.name}: {item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Loss Reasons */}
            <Card data-testid="loss-reasons">
              <CardHeader>
                <CardTitle className="text-lg">Loss Reasons</CardTitle>
                <CardDescription>Why we're losing deals</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={reasonData} layout="vertical">
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={100} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Competitor Performance */}
          <Card data-testid="competitor-performance">
            <CardHeader>
              <CardTitle className="text-lg">Win Rate by Competitor</CardTitle>
              <CardDescription>Our performance against each competitor</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={competitorData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="wins" fill="#22C55E" name="Wins" stackId="a" />
                  <Bar dataKey="losses" fill="#EF4444" name="Losses" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Market Intelligence Feed */}
          <Card data-testid="intel-feed">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Market Intelligence</CardTitle>
                  <CardDescription>Recent competitive insights</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setActiveTab('intelligence')}>
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {intelligence.slice(0, 3).map((intel) => {
                  const ImpactIcon = IMPACT_CONFIG[intel.impact_assessment]?.icon || AlertTriangle;
                  return (
                    <div 
                      key={intel.id} 
                      className={`p-4 rounded-lg border-l-4 ${
                        intel.impact_assessment === 'HIGH' ? 'border-l-red-500 bg-red-50' :
                        intel.impact_assessment === 'MEDIUM' ? 'border-l-yellow-500 bg-yellow-50' :
                        'border-l-blue-500 bg-blue-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <ImpactIcon className={`w-5 h-5 mt-0.5 ${
                            intel.impact_assessment === 'HIGH' ? 'text-red-500' :
                            intel.impact_assessment === 'MEDIUM' ? 'text-yellow-500' : 'text-blue-500'
                          }`} />
                          <div>
                            <p className="font-medium">{intel.title}</p>
                            <p className="text-sm text-muted-foreground mt-1">{intel.summary}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">{intel.type.replace('_', ' ')}</Badge>
                              {intel.relevant_regions?.map(r => (
                                <Badge key={r} variant="secondary" className="text-xs">{r}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        {intel.action_required && (
                          <Badge className="bg-red-100 text-red-700">Action Required</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Competitors Tab */}
        <TabsContent value="competitors" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {competitors.map((competitor) => (
              <Card 
                key={competitor.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedCompetitor(competitor.id)}
                data-testid={`competitor-card-${competitor.id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-lg">{competitor.name}</h3>
                      <Badge variant="outline" className="text-xs mt-1">
                        {competitor.type?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <Building2 className="w-8 h-8 text-gray-300" />
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Regions</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {competitor.regions?.slice(0, 4).map(r => (
                          <Badge key={r} variant="secondary" className="text-xs">{r}</Badge>
                        ))}
                      </div>
                    </div>
                    
                    {competitor.estimated_market_share && (
                      <div>
                        <p className="text-xs text-muted-foreground">Est. Market Share</p>
                        <p className="font-semibold">{competitor.estimated_market_share}%</p>
                      </div>
                    )}
                    
                    <div>
                      <p className="text-xs text-muted-foreground">Key Strengths</p>
                      <p className="text-sm truncate">{competitor.strengths?.[0] || 'N/A'}</p>
                    </div>
                  </div>
                  
                  <Button variant="ghost" size="sm" className="w-full mt-4">
                    <Eye className="w-4 h-4 mr-2" />
                    View Profile
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Win/Loss Analysis Tab */}
        <TabsContent value="win-loss" className="space-y-6">
          {/* Value Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Value Won</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(analytics.summary?.won_value || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Value Lost</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(analytics.summary?.lost_value || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Avg Deal Won</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(analytics.summary?.avg_deal_size_won || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Avg Deal Lost</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(analytics.summary?.avg_deal_size_lost || 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Regional Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Regional Win Rates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(analytics.by_region || []).map((region) => (
                  <div key={region.region} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{region.region}</Badge>
                      <span className="text-sm">
                        {region.wins} wins / {region.losses} losses
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress value={region.win_rate} className="w-32 h-2" />
                      <span className={`font-semibold ${region.win_rate >= 50 ? 'text-green-600' : 'text-red-600'}`}>
                        {region.win_rate}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Competitor Breakdown Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Performance by Competitor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Competitor</th>
                      <th className="text-center py-3 px-4">Wins</th>
                      <th className="text-center py-3 px-4">Losses</th>
                      <th className="text-center py-3 px-4">Retained</th>
                      <th className="text-center py-3 px-4">Win Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics.by_competitor || []).map((comp) => (
                      <tr key={comp.competitor_id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{comp.competitor_name}</td>
                        <td className="text-center py-3 px-4 text-green-600">{comp.wins}</td>
                        <td className="text-center py-3 px-4 text-red-600">{comp.losses}</td>
                        <td className="text-center py-3 px-4 text-blue-600">{comp.retained}</td>
                        <td className="text-center py-3 px-4">
                          <Badge className={comp.win_rate >= 50 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {comp.win_rate}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Market Intel Tab */}
        <TabsContent value="intelligence" className="space-y-4">
          <div className="flex items-center justify-between">
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                <SelectItem value="PJM">PJM</SelectItem>
                <SelectItem value="ERCOT">ERCOT</SelectItem>
                <SelectItem value="CAISO">CAISO</SelectItem>
                <SelectItem value="NYISO">NYISO</SelectItem>
                <SelectItem value="MISO">MISO</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            {intelligence
              .filter(i => regionFilter === 'all' || i.relevant_regions?.includes(regionFilter))
              .map((intel) => {
                const ImpactIcon = IMPACT_CONFIG[intel.impact_assessment]?.icon || AlertTriangle;
                return (
                  <Card key={intel.id} data-testid={`intel-item-${intel.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-lg ${
                            intel.impact_assessment === 'HIGH' ? 'bg-red-100' :
                            intel.impact_assessment === 'MEDIUM' ? 'bg-yellow-100' : 'bg-blue-100'
                          }`}>
                            <ImpactIcon className={`w-5 h-5 ${
                              intel.impact_assessment === 'HIGH' ? 'text-red-600' :
                              intel.impact_assessment === 'MEDIUM' ? 'text-yellow-600' : 'text-blue-600'
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{intel.title}</h3>
                              <Badge className={IMPACT_CONFIG[intel.impact_assessment]?.color}>
                                {intel.impact_assessment}
                              </Badge>
                              {intel.action_required && (
                                <Badge className="bg-red-100 text-red-700">Action Required</Badge>
                              )}
                            </div>
                            <p className="text-muted-foreground mt-2">{intel.summary}</p>
                            <div className="flex items-center gap-2 mt-3">
                              <Badge variant="outline">{intel.type?.replace('_', ' ')}</Badge>
                              {intel.relevant_regions?.map(r => (
                                <Badge key={r} variant="secondary">{r}</Badge>
                              ))}
                              <span className="text-xs text-muted-foreground ml-auto">
                                Source: {intel.source}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Competitor Detail Drawer */}
      <Dialog open={!!selectedCompetitor} onOpenChange={() => setSelectedCompetitor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{competitorDetail?.competitor?.name}</DialogTitle>
            <DialogDescription>Competitor Profile & Analysis</DialogDescription>
          </DialogHeader>
          {competitorDetail?.competitor && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium">{competitorDetail.competitor.type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Market Share</p>
                  <p className="font-medium">{competitorDetail.competitor.estimated_market_share || 'N/A'}%</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground mb-2">Regions</p>
                <div className="flex flex-wrap gap-1">
                  {competitorDetail.competitor.regions?.map(r => (
                    <Badge key={r} variant="secondary">{r}</Badge>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Strengths</p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {competitorDetail.competitor.strengths?.map((s, i) => (
                      <li key={i} className="text-green-700">{s}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Weaknesses</p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {competitorDetail.competitor.weaknesses?.map((w, i) => (
                      <li key={i} className="text-red-700">{w}</li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {competitorDetail.competitor.win_loss_stats && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-2">Our Record Against Them</p>
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-2xl font-bold text-green-600">{competitorDetail.competitor.win_loss_stats.wins}</p>
                      <p className="text-xs text-muted-foreground">Wins</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{competitorDetail.competitor.win_loss_stats.losses}</p>
                      <p className="text-xs text-muted-foreground">Losses</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{competitorDetail.competitor.win_loss_stats.win_rate}%</p>
                      <p className="text-xs text-muted-foreground">Win Rate</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Record Win/Loss Modal */}
      <Dialog open={showWinLossModal} onOpenChange={setShowWinLossModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Win/Loss Outcome</DialogTitle>
            <DialogDescription>Track competitive outcomes for analysis</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Outcome</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WON">Won (New Customer)</SelectItem>
                  <SelectItem value="RETAINED">Retained (Renewal)</SelectItem>
                  <SelectItem value="LOST">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Competitor</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select competitor" />
                </SelectTrigger>
                <SelectContent>
                  {competitors.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                  <SelectItem value="unknown">Unknown/Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Annual Value ($)</Label>
              <Input type="number" placeholder="500000" />
            </div>
            <div>
              <Label>Primary Reason</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRICE">Price</SelectItem>
                  <SelectItem value="SERVICE">Service Quality</SelectItem>
                  <SelectItem value="RELATIONSHIP">Relationship</SelectItem>
                  <SelectItem value="SUSTAINABILITY">Sustainability</SelectItem>
                  <SelectItem value="CONTRACT_TERMS">Contract Terms</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea placeholder="Additional context..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWinLossModal(false)}>Cancel</Button>
            <Button onClick={() => setShowWinLossModal(false)}>Record Outcome</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Intel Modal */}
      <Dialog open={showIntelModal} onOpenChange={setShowIntelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Market Intelligence</DialogTitle>
            <DialogDescription>Share competitive insights with the team</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="COMPETITOR_MOVE">Competitor Move</SelectItem>
                  <SelectItem value="MARKET_TREND">Market Trend</SelectItem>
                  <SelectItem value="REGULATORY">Regulatory</SelectItem>
                  <SelectItem value="PRICING_SIGNAL">Pricing Signal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title</Label>
              <Input placeholder="Brief title..." />
            </div>
            <div>
              <Label>Summary</Label>
              <Textarea placeholder="Key details..." />
            </div>
            <div>
              <Label>Source</Label>
              <Input placeholder="Where did you learn this?" />
            </div>
            <div>
              <Label>Impact Level</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select impact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIntelModal(false)}>Cancel</Button>
            <Button onClick={() => setShowIntelModal(false)}>Add Intelligence</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
